// Serial, debounced writes: an older request can never finish after a newer write.
export class GridPreferenceSaver {
  constructor(save, onError, onSuccess, initialState) {
    this.save = save;
    this.onError = onError;
    this.onSuccess = onSuccess;
    this.lastSaved = JSON.stringify(initialState);
    this.lastAttempted = this.lastSaved;
    this.pending = null;
    this.active = null;
    this.paused = false;
    this.failureNotified = false;
  }

  schedule(state) {
    if (this.disposed) return;
    this.pending = state;
    clearTimeout(this.timer);
    if (!this.paused) this.timer = setTimeout(() => { this.flush(); }, 1000);
  }

  async flush() {
    clearTimeout(this.timer);
    if (this.disposed || this.paused || this.active || !this.pending) return;
    const state = this.pending;
    this.pending = null;
    const serialized = JSON.stringify(state);
    if (serialized === this.lastSaved || serialized === this.lastAttempted) return;
    this.lastAttempted = serialized;
    this.controller = new AbortController();
    this.active = this.save(state, this.controller.signal);
    try {
      await this.active;
      this.lastSaved = serialized;
      this.failureNotified = false;
      if (!this.disposed) this.onSuccess(state);
    } catch (error) {
      if (!this.disposed && !this.failureNotified) {
        this.failureNotified = true;
        this.onError(error);
      }
    } finally {
      this.active = null;
      if (this.pending && !this.paused && !this.disposed) this.flush();
    }
  }

  async pauseAndWait() {
    this.paused = true;
    clearTimeout(this.timer);
    this.pending = null;
    try { await this.active; } catch { /* The active save reports its own error. */ }
  }

  resume() {
    this.paused = false;
    if (this.pending) this.schedule(this.pending);
  }

  dispose() {
    this.disposed = true;
    clearTimeout(this.timer);
    this.pending = null;
    this.controller?.abort();
  }
}
